import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import { MdBarChart, MdChecklist, MdTrackChanges, MdEmojiObjects, MdSync, MdAdd } from 'react-icons/md';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import {
    fetchConditionTypes,
    createConditionType,
    updateConditionType,
    deleteConditionType,
    fetchConditionAttributes,
    createConditionAttribute,
    updateConditionAttribute,
    deleteConditionAttribute,
    fetchTargetTypes,
    createTargetType,
    updateTargetType,
    deleteTargetType,
    fetchTargetAttributes,
    createTargetAttribute,
    updateTargetAttribute,
    deleteTargetAttribute,
    fetchIncentiveTypes,
    createIncentiveType,
    updateIncentiveType,
    deleteIncentiveType,
    fetchIncentiveAttributes,
    createIncentiveAttribute,
    updateIncentiveAttribute,
    deleteIncentiveAttribute,
    fetchStrategyTypes,
    createStrategyType,
    updateStrategyType,
    deleteStrategyType,
    fetchStrategyAttributes,
    createStrategyAttribute,
    updateStrategyAttribute,
    deleteStrategyAttribute,
} from '../../../services/Http/AdminApi';

const tabConfig = {
    conditions: {
        label: 'Conditions',
        labelKey: 'AdminPromotionTypes.tabs.conditions',
        icon: <MdChecklist className="icon" />,
        fetchTypes: fetchConditionTypes,
        createType: createConditionType,
        updateType: updateConditionType,
        deleteType: deleteConditionType,
        fetchAttributes: fetchConditionAttributes,
        createAttribute: createConditionAttribute,
        updateAttribute: updateConditionAttribute,
        deleteAttribute: deleteConditionAttribute,
        attributeKeyLabel: 'Attribute key',
        attributeKeyLabelKey: 'AdminPromotionTypes.attributes.attributeKey',
        attributeNameLabel: 'Attribute name',
        attributeNameLabelKey: 'AdminPromotionTypes.attributes.attributeName',
        supportsFunctionality: true,
    },
    targets: {
        label: 'Targets',
        labelKey: 'AdminPromotionTypes.tabs.targets',
        icon: <MdTrackChanges className="icon" />,
        fetchTypes: fetchTargetTypes,
        createType: createTargetType,
        updateType: updateTargetType,
        deleteType: deleteTargetType,
        fetchAttributes: fetchTargetAttributes,
        createAttribute: createTargetAttribute,
        updateAttribute: updateTargetAttribute,
        deleteAttribute: deleteTargetAttribute,
        attributeKeyLabel: 'Key',
        attributeKeyLabelKey: 'Common.key',
        attributeNameLabel: 'Name',
        attributeNameLabelKey: 'Common.name',
        supportsFunctionality: true,
    },
    incentives: {
        label: 'Incentives',
        labelKey: 'AdminPromotionTypes.tabs.incentives',
        icon: <MdEmojiObjects className="icon" />,
        fetchTypes: fetchIncentiveTypes,
        createType: createIncentiveType,
        updateType: updateIncentiveType,
        deleteType: deleteIncentiveType,
        fetchAttributes: fetchIncentiveAttributes,
        createAttribute: createIncentiveAttribute,
        updateAttribute: updateIncentiveAttribute,
        deleteAttribute: deleteIncentiveAttribute,
        attributeKeyLabel: 'Key',
        attributeKeyLabelKey: 'Common.key',
        attributeNameLabel: 'Name',
        attributeNameLabelKey: 'Common.name',
        supportsFunctionality: false,
    },
    strategies: {
        label: 'Strategies',
        labelKey: 'AdminPromotionTypes.tabs.strategies',
        icon: <MdBarChart className="icon" />,
        fetchTypes: fetchStrategyTypes,
        createType: createStrategyType,
        updateType: updateStrategyType,
        deleteType: deleteStrategyType,
        fetchAttributes: fetchStrategyAttributes,
        createAttribute: createStrategyAttribute,
        updateAttribute: updateStrategyAttribute,
        deleteAttribute: deleteStrategyAttribute,
        attributeKeyLabel: 'Attribute key',
        attributeKeyLabelKey: 'AdminPromotionTypes.attributes.attributeKey',
        attributeNameLabel: 'Attribute name',
        attributeNameLabelKey: 'AdminPromotionTypes.attributes.attributeName',
        supportsFunctionality: false,
    },
};

const AdminPromotionTypes = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('conditions');
    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('AdminPromotionTypes.pageTitle')}</PageTitle>
            </PageHeader>
            <TabBar>
                {Object.entries(tabConfig).map(([key, config]) => (
                    <TabButton key={key} active={activeTab === key} onClick={() => setActiveTab(key)} icon={config.icon}>
                        {t(config.labelKey, config.label)}
                    </TabButton>
                ))}
            </TabBar>
            <PageContent>
                <TypeManager config={tabConfig[activeTab]} />
            </PageContent>
        </PageContainer>
    );
};

const TypeManager = ({ config }) => {
    const { t } = useTranslation();
    const [types, setTypes] = useState([]);
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const [attributes, setAttributes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [attrLoading, setAttrLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [showAttrModal, setShowAttrModal] = useState(false);
    const [confirmDeleteTypeOpen, setConfirmDeleteTypeOpen] = useState(false);
    const [typeForm, setTypeForm] = useState(defaultTypeForm());
    const [attrForm, setAttrForm] = useState(defaultAttrForm());
    const [editingTypeId, setEditingTypeId] = useState(null);
    const [editingAttrId, setEditingAttrId] = useState(null);

    useEffect(() => {
        setSelectedTypeId(null);
        loadTypes();
    }, [config]);

    useEffect(() => {
        if (!selectedTypeId) {
            setAttributes([]);
            return;
        }
        loadAttributes(selectedTypeId);
    }, [selectedTypeId]);

    const loadTypes = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await config.fetchTypes();
            setTypes(Array.isArray(data) ? data : []);
            setSelectedTypeId(data?.length ? data[0].id : null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const loadAttributes = async (typeId) => {
        try {
            setAttrLoading(true);
            const data = await config.fetchAttributes(typeId);
            setAttributes(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err);
        } finally {
            setAttrLoading(false);
        }
    };

    const handleSaveType = async (e) => {
        e.preventDefault();
        try {
            if (editingTypeId) {
                await config.updateType(editingTypeId, typeForm);
            } else {
                await config.createType(typeForm);
            }
            setShowTypeModal(false);
            setEditingTypeId(null);
            setTypeForm(defaultTypeForm());
            await loadTypes();
        } catch (err) {
            setError(err);
        }
    };

    const handleSaveAttribute = async (e) => {
        e.preventDefault();
        if (!selectedTypeId) return;
        try {
            if (editingAttrId) {
                await config.updateAttribute(selectedTypeId, editingAttrId, attrForm);
            } else {
                await config.createAttribute(selectedTypeId, attrForm);
            }
            setShowAttrModal(false);
            setEditingAttrId(null);
            setAttrForm(defaultAttrForm());
            await loadAttributes(selectedTypeId);
        } catch (err) {
            setError(err);
        }
    };

    const typeColumns = useMemo(() => [
        { name: t('Common.name'), selector: (row) => row.name, sortable: true },
        { name: t('Common.key'), selector: (row) => row.key, sortable: true },
        { name: t('AdminPromotionTypes.columns.targetModel'), selector: (row) => row.target_model || '-', sortable: false },
        { name: t('AdminPromotionTypes.columns.attributes'), selector: (row) => (row.attributes || []).length, sortable: false },
    ], [t]);

    const attrColumns = useMemo(() => [
        { name: t(config.attributeNameLabelKey, config.attributeNameLabel), selector: (row) => row.attribute_name || row.name, sortable: true },
        { name: t(config.attributeKeyLabelKey, config.attributeKeyLabel), selector: (row) => row.attribute_key || row.key, sortable: true },
        { name: t('AdminPromotionTypes.columns.dataType'), selector: (row) => row.data_type || '-', sortable: true },
        {
            name: t('AdminPromotionTypes.columns.operators'),
            selector: (row) => Array.isArray(row.supported_operators) ? row.supported_operators.join(', ') : '-',
            sortable: false,
        },
    ], [config.attributeKeyLabel, config.attributeKeyLabelKey, config.attributeNameLabel, config.attributeNameLabelKey, t]);

    if (loading) return <LoadingMessage>{t('AdminPromotionTypes.loading', { entity: t(config.labelKey, config.label).toLowerCase() })}</LoadingMessage>;
    if (error) return <ErrorMessage>{t('AdminPromotionTypes.loadError', { entity: t(config.labelKey, config.label).toLowerCase() })}</ErrorMessage>;

    return (
        <>
            <ActionsBar>
                <PrimaryButton type="button" onClick={() => { setEditingTypeId(null); setTypeForm(defaultTypeForm()); setShowTypeModal(true); }}>
                    <MdAdd size={18} />
                    {t('AdminPromotionTypes.actions.addEntity', { entity: t(config.labelKey, config.label).slice(0, -1) })}
                </PrimaryButton>
                <GhostButton type="button" onClick={loadTypes}>
                    <MdSync size={18} />
                    {t('Common.refresh')}
                </GhostButton>
            </ActionsBar>
            <Section>
                <SectionHeader>{t(config.labelKey, config.label)}</SectionHeader>
                <DataTable
                    columns={typeColumns}
                    data={types}
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={(row) => setSelectedTypeId(row.id)}
                    selectableRowsSingle
                    selectableRowsHighlight
                    selectableRows
                    onSelectedRowsChange={({ selectedRows }) => setSelectedTypeId(selectedRows[0]?.id ?? null)}
                />
                <InlineActions>
                    <GhostButton
                        type="button"
                        disabled={!selectedTypeId}
                        onClick={() => {
                            if (!selectedTypeId) return;
                            const type = types.find((t) => t.id === selectedTypeId);
                            setEditingTypeId(type?.id || null);
                            setTypeForm({
                                name: type?.name || '',
                                key: type?.key || '',
                                target_model: type?.target_model || '',
                            });
                            setShowTypeModal(true);
                        }}
                    >
                        {t('AdminPromotionTypes.actions.editSelected')}
                    </GhostButton>
                    <GhostButton
                        type="button"
                        disabled={!selectedTypeId}
                        onClick={async () => {
                            if (!selectedTypeId) return;
                            setConfirmDeleteTypeOpen(true);
                        }}
                    >
                        {t('AdminPromotionTypes.actions.deleteSelected')}
                    </GhostButton>
                </InlineActions>
            </Section>

            <Section>
                <SectionHeader>{t('AdminPromotionTypes.attributes.title')}</SectionHeader>
                {attrLoading ? (
                    <LoadingMessage>{t('AdminPromotionTypes.attributes.loading')}</LoadingMessage>
                ) : (
                    <DataTable
                        columns={attrColumns}
                        data={attributes}
                        highlightOnHover
                        noDataComponent={selectedTypeId ? t('AdminPromotionTypes.attributes.empty') : t('AdminPromotionTypes.attributes.selectTypeHint')}
                        onRowClicked={(row) => {
                            setEditingAttrId(row.id);
                            setAttrForm({
                                name: row.name || row.attribute_name || '',
                                key: row.key || row.attribute_key || '',
                                attribute_name: row.attribute_name || row.name || '',
                                attribute_key: row.attribute_key || row.key || '',
                                data_type: row.data_type || '',
                                supported_operators: row.supported_operators || [],
                                is_required: row.is_required ?? false,
                                condition_functionality: row.condition_functionality || '',
                            });
                            setShowAttrModal(true);
                        }}
                    />
                )}
                <InlineActions>
                    <PrimaryButton
                        type="button"
                        disabled={!selectedTypeId}
                        onClick={() => {
                            setEditingAttrId(null);
                            setAttrForm(defaultAttrForm());
                            setShowAttrModal(true);
                        }}
                    >
                        <MdAdd size={18} />
                        {t('AdminPromotionTypes.attributes.add')}
                    </PrimaryButton>
                </InlineActions>
            </Section>

            {showTypeModal && (
                <ModalOverlay>
                    <ModalCard>
                        <ModalHeader>
                            <h4>{editingTypeId ? t('AdminPromotionTypes.typeModal.editTitle') : t('AdminPromotionTypes.typeModal.addTitle')}</h4>
                            <CloseButton type="button" onClick={() => setShowTypeModal(false)}>✕</CloseButton>
                        </ModalHeader>
                        <Form onSubmit={handleSaveType}>
                            <Row>
                                <Input name="name" value={typeForm.name} onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('Common.name')} required />
                                <Input name="key" value={typeForm.key} onChange={(e) => setTypeForm((f) => ({ ...f, key: e.target.value }))} placeholder={t('Common.key')} required />
                            </Row>
                            {'target_model' in typeForm && (
                                <Row>
                                    <Input name="target_model" value={typeForm.target_model} onChange={(e) => setTypeForm((f) => ({ ...f, target_model: e.target.value }))} placeholder={t('AdminPromotionTypes.typeModal.targetModelOptional')} />
                                </Row>
                            )}
                            <Actions>
                                <PrimaryButton type="submit">{t('Common.save')}</PrimaryButton>
                                <GhostButton type="button" onClick={() => setShowTypeModal(false)}>{t('Common.cancel')}</GhostButton>
                            </Actions>
                        </Form>
                    </ModalCard>
                </ModalOverlay>
            )}

            {showAttrModal && (
                <ModalOverlay>
                    <ModalCard>
                        <ModalHeader>
                            <h4>{editingAttrId ? t('AdminPromotionTypes.attributeModal.editTitle') : t('AdminPromotionTypes.attributeModal.addTitle')}</h4>
                            <CloseButton type="button" onClick={() => setShowAttrModal(false)}>✕</CloseButton>
                        </ModalHeader>
                        <Form onSubmit={handleSaveAttribute}>
                            <Row>
                                <Input
                                    name="attribute_name"
                                    value={attrForm.attribute_name || attrForm.name}
                                    onChange={(e) => setAttrForm((f) => ({ ...f, attribute_name: e.target.value, name: e.target.value }))}
                                    placeholder={t(config.attributeNameLabelKey, config.attributeNameLabel)}
                                    required
                                />
                                <Input
                                    name="attribute_key"
                                    value={attrForm.attribute_key || attrForm.key}
                                    onChange={(e) => setAttrForm((f) => ({ ...f, attribute_key: e.target.value, key: e.target.value }))}
                                    placeholder={t(config.attributeKeyLabelKey, config.attributeKeyLabel)}
                                    required
                                />
                            </Row>
                            <Row>
                                <Input
                                    name="data_type"
                                    value={attrForm.data_type}
                                    onChange={(e) => setAttrForm((f) => ({ ...f, data_type: e.target.value }))}
                                    placeholder={t('AdminPromotionTypes.attributeModal.dataTypePlaceholder')}
                                    required
                                />
                                <Input
                                    name="supported_operators"
                                    value={(attrForm.supported_operators || []).join(', ')}
                                    onChange={(e) => setAttrForm((f) => ({ ...f, supported_operators: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) }))}
                                    placeholder={t('AdminPromotionTypes.attributeModal.supportedOperatorsPlaceholder')}
                                />
                            </Row>
                            {config.supportsFunctionality && (
                                <Row>
                                    <Input
                                        name="condition_functionality"
                                        value={attrForm.condition_functionality || ''}
                                        onChange={(e) => setAttrForm((f) => ({ ...f, condition_functionality: e.target.value }))}
                                        placeholder={t('AdminPromotionTypes.attributeModal.functionalityOptional')}
                                    />
                                </Row>
                            )}
                            <Row>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={!!attrForm.is_required}
                                        onChange={(e) => setAttrForm((f) => ({ ...f, is_required: e.target.checked }))}
                                    />
                                    <span style={{ marginLeft: 8 }}>{t('Common.required')}</span>
                                </label>
                            </Row>
                            <Actions>
                                <PrimaryButton type="submit">{t('AdminPromotionTypes.attributeModal.save')}</PrimaryButton>
                                <GhostButton type="button" onClick={() => setShowAttrModal(false)}>{t('Common.cancel')}</GhostButton>
                            </Actions>
                        </Form>
                    </ModalCard>
                </ModalOverlay>
            )}
            <StandardModal
                isOpen={confirmDeleteTypeOpen}
                onClose={() => setConfirmDeleteTypeOpen(false)}
                title={t('AdminPromotionTypes.deleteModal.title')}
                footer={(
                    <>
                        <GhostButton type="button" onClick={() => setConfirmDeleteTypeOpen(false)}>{t('Common.cancel')}</GhostButton>
                        <PrimaryButton
                            type="button"
                            onClick={async () => {
                                if (!selectedTypeId) return;
                                setConfirmDeleteTypeOpen(false);
                                await config.deleteType(selectedTypeId);
                                setSelectedTypeId(null);
                                await loadTypes();
                            }}
                        >
                            {t('Common.delete')}
                        </PrimaryButton>
                    </>
                )}
            >
                <p style={{ margin: 0 }}>{t('AdminPromotionTypes.deleteModal.body', { entity: t(config.labelKey, config.label).slice(0, -1).toLowerCase() })}</p>
            </StandardModal>
        </>
    );
};

const defaultTypeForm = () => ({ name: '', key: '', target_model: '' });
const defaultAttrForm = () => ({
    attribute_name: '',
    attribute_key: '',
    data_type: '',
    supported_operators: [],
    is_required: false,
    condition_functionality: '',
});

const ActionsBar = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.625rem;
    flex-wrap: wrap;
`;

const Section = styled.div`
    margin-bottom: 1rem;
`;

const SectionHeader = styled.h4`
    margin: 0 0 0.5rem;
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.625rem 0.875rem;
    cursor: pointer;
    font-weight: 700;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
`;

const InlineActions = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    z-index: 2000;
`;

const ModalCard = styled.div`
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 12px;
    padding: 1rem;
    width: min(720px, 100%);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.625rem;
`;

const CloseButton = styled.button`
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 8px;
    padding: 0.375rem 0.625rem;
    cursor: pointer;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;
`;

const Input = styled.input`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 0.625rem;
`;

export default AdminPromotionTypes;
